// PHASE_1 / B3 — pure tree-windowing helpers for the virtualized OrgChart.
//
// Extracted from OrgChart.tsx so the load-bearing flatten/index logic (the
// windowing exit gate) is unit-testable without a DOM. indexTree builds
// parent→children adjacency from the flat solid-line node list; flatten turns
// the tree into the LINEAR visible-row list honoring the expanded set
// (lazy-expand — a collapsed subtree's descendants never enter the list). Both
// are pure + iterative (explicit stack) so a deep tree can't blow the call
// stack. Solid-line only (ADR-012); dotted-line rows are NOT in the chart.

import type { OrgChartNode } from "./api";

export interface FlatRow {
  readonly node: OrgChartNode;
  readonly depth: number;
  readonly hasChildren: boolean;
  readonly expanded: boolean;
}

export interface IndexedTree {
  readonly childrenOf: Map<string | null, OrgChartNode[]>;
  readonly roots: OrgChartNode[];
}

/** Build parent→children adjacency + the ordered roots from the flat node list. */
export function indexTree(nodes: readonly OrgChartNode[]): IndexedTree {
  const byId = new Set(nodes.map((n) => n.employeeId));
  const childrenOf = new Map<string | null, OrgChartNode[]>();
  for (const n of nodes) {
    // A node whose manager isn't in the set (orphan / cross-page) is treated as
    // a root so it never disappears from the chart.
    const key = n.managerId !== null && byId.has(n.managerId) ? n.managerId : null;
    const arr = childrenOf.get(key);
    if (arr === undefined) childrenOf.set(key, [n]);
    else arr.push(n);
  }
  for (const arr of childrenOf.values()) {
    arr.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }
  return { childrenOf, roots: childrenOf.get(null) ?? [] };
}

/**
 * Flatten the tree into the LINEAR visible-row list, honoring `expanded`. A
 * collapsed node contributes its own row but none of its descendants — lazy
 * expand. Iterative (explicit stack) so a deep tree can't blow the call stack.
 */
export function flatten(
  roots: readonly OrgChartNode[],
  childrenOf: Map<string | null, OrgChartNode[]>,
  expanded: ReadonlySet<string>,
): FlatRow[] {
  const out: FlatRow[] = [];
  const stack: { node: OrgChartNode; depth: number }[] = [];
  for (let i = roots.length - 1; i >= 0; i -= 1) {
    stack.push({ node: roots[i]!, depth: 0 });
  }
  while (stack.length > 0) {
    const { node, depth } = stack.pop()!;
    const children = childrenOf.get(node.employeeId) ?? [];
    const hasChildren = children.length > 0;
    const isExpanded = expanded.has(node.employeeId);
    out.push({ node, depth, hasChildren, expanded: isExpanded });
    if (hasChildren && isExpanded) {
      for (let i = children.length - 1; i >= 0; i -= 1) {
        stack.push({ node: children[i]!, depth: depth + 1 });
      }
    }
  }
  return out;
}

/**
 * The visible window of rows for a given scroll position. Pure so the
 * windowing math (the ~1000-node exit gate) is testable without a DOM: returns
 * the slice indices intersecting `[scrollTop, scrollTop + viewportHeight]` plus
 * `overscan` rows of margin. At 1000 rows this slice is ~30, not 1000.
 */
export function windowSlice(args: {
  readonly total: number;
  readonly scrollTop: number;
  readonly viewportHeight: number;
  readonly rowHeight: number;
  readonly overscan: number;
}): { readonly startIndex: number; readonly endIndex: number } {
  const { total, scrollTop, viewportHeight, rowHeight, overscan } = args;
  const visibleCount = Math.ceil(viewportHeight / rowHeight);
  const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const endIndex = Math.min(total, startIndex + visibleCount + overscan * 2);
  return { startIndex, endIndex };
}
